"use client"

import { HttpTypes } from "@medusajs/types"
import { Text } from "@/components/common"
import { useLocale } from "next-intl"
import LocalizedClientLink from "@modules/common/components/localized-client-link"
import { getLocalizedProductContent } from "@lib/util/product-localization"
import type { ProductTypeDetails } from "@/types/product-details"

type ProductInfoProps = {
  product: HttpTypes.StoreProduct
  typeDetails?: ProductTypeDetails | null
}

/** Strip HTML tags, converting <br> and </p> to newlines */
function stripHtml(text: string): string {
  return text
    .replace(/<br\s*\/?>/gi, "\n")
    .replace(/<\/p>/gi, "\n")
    .replace(/<[^>]+>/g, "")
    .replace(/&nbsp;/g, " ")
    .replace(/&amp;/g, "&")
    .replace(/&lt;/g, "<")
    .replace(/&gt;/g, ">")
    .replace(/\n{3,}/g, "\n\n")
    .trim()
}

const ProductInfo = ({ product, typeDetails }: ProductInfoProps) => {
  const locale = useLocale()
  const localizedContent = getLocalizedProductContent(product, locale as any)

  // Primary source: type-details localized name/title (from game_translation / book_translation).
  // Fallback: product-level localization (from middleware-injected translated_title or product.title).
  let title = localizedContent.title
  let description = localizedContent.description

  if (typeDetails && typeDetails.product_type !== null && typeDetails.localized) {
    const localized = typeDetails.localized
    if (typeDetails.product_type === "game" && "name" in localized && localized.name) {
      title = localized.name
    }
    if (typeDetails.product_type === "book" && "title" in localized && localized.title) {
      title = localized.title
    }
    if (localized.description) {
      description = localized.description
    }
  }

  return (
    <div id="product-info">
      <div className="flex flex-col gap-y-4">
        {product.collection && (
          <LocalizedClientLink
            href={`/collections/${product.collection.handle}`}
            className="text-sm text-muted-foreground hover:text-foreground transition-colors"
          >
            {product.collection.title}
          </LocalizedClientLink>
        )}
        <h1
          className="text-3xl md:text-4xl font-bold font-serif text-foreground tracking-tight"
          data-testid="product-title"
        >
          {title}
        </h1>

        {description && (
          <Text
            className="text-base text-foreground-secondary leading-relaxed whitespace-pre-line"
            data-testid="product-description"
          >
            {stripHtml(description)}
          </Text>
        )}
      </div>
    </div>
  )
}

export default ProductInfo
